import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, errorText } from "../api";
import { useAccess } from "../access";
import { useBusiness } from "../business";
import { useSession } from "../session";
import { Badge, Empty, Icon, fmtDateTime } from "../ui";
import { NeedsYou } from "../needs";
import { HomeMetrics } from "../home-start";

/**
 * Home is the chief of staff: "Mivio" in every business, "Alfred" at Mivio HQ. Ask anything about the business,
 * or hand over something to do; the chief suggests, you approve.
 */

interface Message { id: number; role: "user" | "assistant"; author: string; content: string; at: string }
const MAX = 4000;

function greeting(name: string): string {
  const h = new Date().getHours();
  return `${h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"}, ${name.split(" ")[0] ?? ""}`;
}

function quickAsks(solutions: readonly string[]): string[] {
  const out = ["What should I focus on this week?"];
  if (solutions.includes("events")) out.push("How's the next event looking?");
  if (solutions.includes("loops")) out.push("Who's close to their next card?");
  if (solutions.includes("people")) out.push("Who joined lately?");
  out.push("What changed lately?");
  return out;
}

export function HomePage() {
  const { me } = useSession();
  const { data } = useBusiness();
  const { can } = useAccess();
  const qc = useQueryClient();
  const hq = data.business.kind === "hq";
  const list = useQuery({ queryKey: ["chief", "conversations"], queryFn: () => api<{ chief: string; conversations: { id: string; title: string; updatedAt: string }[] }>("/chief/conversations") });
  const name = list.data?.chief ?? (hq ? "Alfred" : "Mivio");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [fresh, setFresh] = useState(false);
  const latest = fresh ? null : (conversationId ?? list.data?.conversations[0]?.id ?? null);
  const thread = useQuery({
    queryKey: ["chief", "messages", latest],
    queryFn: () => api<{ messages: Message[] }>(`/chief/conversations/${latest}`).then((r) => r.messages),
    enabled: !!latest,
  });
  const [messages, setMessages] = useState<Message[]>([]);
  useEffect(() => {
    if (thread.data) setMessages(thread.data);
  }, [thread.data]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<{ code: string; text: string } | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const thinking = pending !== null;

  useEffect(() => {
    const el = scroller.current;
    el?.scrollTo?.({ top: el.scrollHeight });
  }, [messages.length, thinking]);

  const submit = async (raw: string) => {
    const message = raw.trim().slice(0, MAX);
    if (!message || thinking) return;
    setError(null);
    setText("");
    setPending(message);
    try {
      const r = await api<{ conversationId: string; reply: Message }>("/chief/messages", { method: "POST", json: { message, ...(latest ? { conversationId: latest } : {}) } });
      const mine: Message = { id: -Date.now(), role: "user", author: "You", content: message, at: new Date().toISOString() };
      setMessages((m) => [...m, mine, r.reply]);
      setConversationId(r.conversationId);
      setFresh(false);
      void qc.invalidateQueries({ queryKey: ["chief", "conversations"] });
    } catch (err) {
      setText(message);
      setError({ code: err instanceof ApiError ? err.code : "error", text: errorText(err) });
    } finally {
      setPending(null);
      box.current?.focus({ preventScroll: true });
    }
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit(text);
    }
  };
  const rows = Math.min(5, Math.max(1, text.split("\n").length));
  const loading = list.isPending || (!!latest && thread.isPending);

  const starting = !loading && messages.length === 0 && !pending;
  const composer = (
    <form className={`st-composer${starting ? " home-ask" : ""}`} onSubmit={(e) => { e.preventDefault(); void submit(text); }}>
      {starting && (
        <span className="home-ask__icon" aria-hidden="true">
          <Icon.sparkle />
        </span>
      )}
      <textarea ref={box} className="st-input" value={text} rows={rows} maxLength={MAX} placeholder={starting ? `Ask ${name} anything…` : `Ask ${name}…`} aria-label={`Your message to ${name}`} disabled={thinking} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
      <button type="submit" className="btn btn--primary st-send" disabled={thinking || !text.trim()}>
        {thinking ? "Sending…" : "Send"}
      </button>
    </form>
  );
  const quick = (
    <div className="st-quick" role="group" aria-label="Quick asks">
      {quickAsks(data.solutions ?? []).map((x) => (
        <button key={x} type="button" className="chip-toggle st-quick__chip" disabled={thinking} onClick={() => void submit(x)}>
          {x}
        </button>
      ))}
    </div>
  );

  // Before a conversation: the month at a glance, then the greeting and the ask box in the middle of the page.
  if (starting) {
    return (
      <div className="home-start">
        {!hq && <HomeMetrics />}
        <div className="home-start__middle">
          <span className="st-avatar st-avatar--chief home-start__face" aria-hidden="true">
            <Icon.sparkle />
          </span>
          <h1 className="home-start__title">
            {greeting(me?.account.name ?? "")}
            <span>{hq ? "Let's keep building Mivio." : `Let's keep ${data.business.name} growing.`}</span>
          </h1>
          {composer}
          {error && (
            <p className="error st-error" role="alert">
              {error.text}{" "}
              {error.code === "ai_not_connected" && can("business.connectors") && (
                <Link className="link" to="/settings/connectors">
                  Connect AI
                </Link>
              )}
            </p>
          )}
          {!hq && <NeedsYou bare />}
          {quick}
          <p className="st-hint muted">
            I'm {name}, {hq ? "Mivio's chief of staff" : `${data.business.name}'s chief of staff`}. I suggest; you approve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="al-homepage">
      <header className="al-homepage__head">
        <span className="st-avatar st-avatar--chief al-homepage__face" aria-hidden="true">
          <Icon.sparkle />
        </span>
        <div className="al-homepage__who">
          <h1 className="page__title al-homepage__title">{greeting(me?.account.name ?? "")}</h1>
          <p className="muted al-homepage__sub">
            I'm {name}, {hq ? "Mivio's chief of staff" : `${data.business.name}'s chief of staff`}. Ask me anything about {hq ? "Mivio" : "the business"}, or hand me something to do.
          </p>
        </div>
        {messages.length > 0 && (
          <button type="button" className="btn btn--sm" onClick={() => { setFresh(true); setConversationId(null); setMessages([]); setError(null); }}>
            New conversation
          </button>
        )}
      </header>
      {!hq && <NeedsYou />}
      <section className="st-chat al-chat al-chat--home" aria-label={`Chat with ${name}`}>
        <div ref={scroller} className="st-msgs" role="log" aria-live="polite" aria-relevant="additions">
          {loading ? (
            <div className="loading">Opening the chat…</div>
          ) : messages.length === 0 && !pending ? (
            <Empty title={`Say hello to ${name}.`} text={`Ask about ${data.business.name}: what to focus on this week, how to set something up, or what changed. ${name} suggests; you approve.`} />
          ) : null}
          {messages.map((m) => (
            <article key={m.id} className={`st-msg ${m.role === "user" ? "st-msg--person" : "st-msg--chief"}`}>
              {m.role !== "user" && <span className="st-msg__who muted">{m.author || name}</span>}
              <div className="st-msg__bubble">
                {m.content.split(/\n{2,}/).map((p, i) => (
                  <p key={i} className="st-msg__body">{p}</p>
                ))}
              </div>
              <time className="st-msg__when muted" dateTime={m.at} title={fmtDateTime(m.at)}>
                {fmtDateTime(m.at)}
              </time>
            </article>
          ))}
          {pending && (
            <article className="st-msg st-msg--person is-pending">
              <div className="st-msg__bubble">
                <p className="st-msg__body">{pending}</p>
              </div>
              <span className="st-msg__when muted">sending…</span>
            </article>
          )}
          {thinking && (
            <div className="st-thinking" role="status">
              <i className="st-spin" aria-hidden="true" />
              {name} is thinking
            </div>
          )}
        </div>
        {error && (
          <p className="error st-error" role="alert">
            {error.text}{" "}
            {error.code === "ai_not_connected" && can("business.connectors") && (
              <Link className="link" to="/settings/connectors">
                Connect AI
              </Link>
            )}
          </p>
        )}
        {quick}
        {composer}
        <p className="st-hint muted">
          Enter sends; Shift+Enter for a new line.{" "}
          {data.me.owner && <Badge tone="neutral">{hq ? "Mivio HQ" : "Owner"}</Badge>}
        </p>
      </section>
    </div>
  );
}
